import { useRef } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { formatClock, formatMinutes, watchedPercent, mediaUrl } from '../../../../shared/courses'

import styles from './style.module.scss'

/**
 * Eine Reihe je Modul: die Lektionen als Kacheln nebeneinander, am Ende das
 * Quiz. Auf dem Desktop blättern Pfeile, auf dem Telefon wischt man.
 *
 * Die Pfeile schieben um eine Bildschirmbreite weniger einer Kachel — so steht
 * nach dem Blättern die letzte gesehene Kachel noch am Rand, und man verliert
 * den Faden nicht.
 */
export default function ModuleRow({ module, index, onOpenLesson, onOpenQuiz, questionsPerQuiz }) {
	const { t } = useTranslation()
	const trackRef = useRef(null)

	const blaettern = richtung => {
		const el = trackRef.current
		if (!el) return
		el.scrollBy({ left: richtung * Math.max(300, el.clientWidth - 300), behavior: 'smooth' })
	}

	const lessons = module.lessons || []
	const erledigt = lessons.filter(lesson => lesson.progress?.completed).length
	const quiz = module.quiz || {}

	return (
		<section className={styles.row} aria-label={module.title}>
			<div className={styles.rowHead}>
				<h2 className={styles.rowTitle}>
					{t('Module {{n}}', { n: index + 1 })} · {module.title}
				</h2>
				<span className={styles.rowMeta}>
					{t('{{done}} of {{total}} watched', { done: erledigt, total: lessons.length })}
					{module.durationSec ? ` · ${formatMinutes(module.durationSec, t)}` : ''}
				</span>
			</div>

			<div className={styles.rowViewport}>
				<button
					type="button"
					className={clsx(styles.arrow, styles.arrowLeft)}
					onClick={() => blaettern(-1)}
					aria-label={t('Scroll left')}
				>
					‹
				</button>

				<div className={styles.track} ref={trackRef}>
					{lessons.map((lesson, nr) => {
						const prozent = lesson.progress?.completed
							? 100
							: watchedPercent(lesson.progress?.maxPosition, lesson.durationSec)

						return (
							<button
								key={lesson.id}
								type="button"
								className={styles.card}
								onClick={() => onOpenLesson(lesson.id)}
							>
								<div
									className={styles.thumb}
									style={lesson.thumbUrl ? { backgroundImage: `url("${mediaUrl(lesson.thumbUrl)}")` } : undefined}
								>
									{!lesson.thumbUrl && <span className={styles.thumbNo}>{nr + 1}</span>}
									{lesson.kind === 'VIDEO' && lesson.durationSec > 0 && (
										<span className={styles.duration}>{formatClock(lesson.durationSec)}</span>
									)}
									{lesson.progress?.completed && (
										<span className={styles.check} aria-label={t('Watched')}>
											✓
										</span>
									)}
								</div>
								<div className={styles.cardBar} aria-hidden="true">
									<span style={{ width: `${prozent}%` }} />
								</div>
								<div className={styles.cardBody}>
									<p className={styles.cardTitle}>
										{nr + 1}. {lesson.title}
									</p>
									<span className={styles.cardText}>
										{lesson.kind === 'TEXT' ? t('Reading') : t('Video')}
									</span>
								</div>
							</button>
						)
					})}

					{quiz.available && (
						<button
							type="button"
							className={clsx(styles.card, !quiz.unlocked && styles.cardLocked)}
							onClick={() => quiz.unlocked && onOpenQuiz(module.id)}
							aria-disabled={!quiz.unlocked}
							title={quiz.unlocked ? undefined : t('Watch all lessons of this module first.')}
						>
							<div
								className={clsx(
									styles.quizThumb,
									quiz.passed && styles.quizThumbPassed,
									!quiz.unlocked && styles.quizThumbLocked
								)}
							>
								<span className={styles.quizIcon} aria-hidden="true">
									{quiz.passed ? '★' : quiz.unlocked ? '?' : '🔒'}
								</span>
								{t('Quiz')}
							</div>
							<div className={styles.cardBar} aria-hidden="true">
								<span style={{ width: quiz.passed ? '100%' : '0%' }} />
							</div>
							<div className={styles.cardBody}>
								<p className={styles.cardTitle}>
									{t('Quiz · {{n}} questions', { n: questionsPerQuiz })}
								</p>
								<span className={styles.cardText}>
									{quiz.passed
										? t('Passed ({{correct}} of {{total}})', {
												correct: quiz.best?.correct,
												total: quiz.best?.total
										  })
										: quiz.unlocked
										? quiz.attempts > 0
											? t('Not passed yet — try again')
											: t('Ready to start')
										: t('Unlocks after all lessons')}
								</span>
							</div>
						</button>
					)}
				</div>

				<button
					type="button"
					className={clsx(styles.arrow, styles.arrowRight)}
					onClick={() => blaettern(1)}
					aria-label={t('Scroll right')}
				>
					›
				</button>
			</div>
		</section>
	)
}

ModuleRow.propTypes = {
	module: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	onOpenLesson: PropTypes.func.isRequired,
	onOpenQuiz: PropTypes.func.isRequired,
	questionsPerQuiz: PropTypes.number
}

ModuleRow.defaultProps = {
	questionsPerQuiz: 5
}
